import { Flex } from "styled-system/jsx";
import { SelectableRadioCard } from "shared/components";
import type { EventSessionOption } from "shared/utils";

type SessionAvailability = {
  isUnlimited?: boolean | null;
  remainingCapacity?: number | null;
};

type EventTimeSlotSelectorProps = {
  slots: EventSessionOption[];
  value: string[];
  onChange: (next: string[]) => void;
  multiple?: boolean;
  maxCount?: number;
  availabilityBySessionId: Record<string, SessionAvailability | undefined>;
};

const slotSoldOut = (
  availability: SessionAvailability | undefined,
): boolean =>
  !availability?.isUnlimited && (availability?.remainingCapacity ?? 0) <= 0;

export const EventTimeSlotSelector = ({
  slots,
  value,
  onChange,
  multiple = false,
  maxCount,
  availabilityBySessionId,
}: EventTimeSlotSelectorProps) => {
  const atCap = maxCount != null && value.length >= maxCount;
  const capLocks = atCap && maxCount !== 1;
  const isMultiSelect = multiple && maxCount !== 1;

  const pick = (sessionId: string) => {
    if (!multiple) {
      onChange([sessionId]);
      return;
    }
    if (value.includes(sessionId)) {
      onChange(value.filter(id => id !== sessionId));
      return;
    }
    // A 1-pick "multi" behaves like single select: replace freely.
    if (maxCount === 1) {
      onChange([sessionId]);
      return;
    }
    if (atCap) return;
    onChange([...value, sessionId]);
  };

  return (
    <Flex flexDirection="column" gap="100">
      {slots.map(slot => {
        const soldOut = slotSoldOut(availabilityBySessionId[slot.sessionId]);
        const selected = value.includes(slot.sessionId);
        return (
          <SelectableRadioCard
            key={slot.sessionId}
            selectedValue={selected ? slot.sessionId : ""}
            radioValue={slot.sessionId}
            onSelectRadio={() => pick(slot.sessionId)}
            title={slot.timeLabel ?? slot.dateISO}
            selectionIndicator={isMultiSelect ? "checkbox" : "none"}
            disabled={soldOut || (multiple && capLocks && !selected)}
          />
        );
      })}
    </Flex>
  );
};
